import axios from 'axios';
import { API_KEY } from '../../config/env';
import { encrypt, decrypt } from '../../utils/crypto';
import i18n from '../../i18n/i18n';
import { toast } from 'react-toastify';

const languageMap = { tr: 1, en: 2, us: 2 };

const buildHeaders = (isFileUpload = false, tokenOverride = null) => {
  const headers = {
    accept: 'text/plain',
    ChannelId: '1',
    'x-root': `${API_KEY}`,
    'Content-Type': isFileUpload ? 'multipart/form-data' : 'application/json',
  };

  const selectedLanguage = localStorage.getItem('i18nextLng') || 'tr';
  const langCode = languageMap[selectedLanguage];
  if (langCode) {
    headers.lang = String(langCode);
    headers.language = selectedLanguage;
  }

  if (tokenOverride) {
    headers.Authorization = `Bearer ${tokenOverride}`;
    return headers;
  }

  try {
    const encrypted = localStorage.getItem('crmToken');
    if (encrypted) {
      const token = decrypt(encrypted);
      if (token) headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    localStorage.removeItem('crmToken');
  }

  return headers;
};

/** Odazen sitesi (tüketici) oturum token'ı — CRM hesap bağlama uçları için */
export const getOdazenSiteToken = () => {
  try {
    const encrypted = localStorage.getItem('authToken');
    return encrypted ? decrypt(encrypted) : null;
  } catch {
    return null;
  }
};

// Aynı hata metni ekrandayken tekrar açılmasın: crmHttp hatayı zaten gösterdiği için
// sayfalar da toast.error(res.message) çağırınca mesaj iki kez çıkıyordu.
if (!toast.error.__crmDeduped) {
  const originalError = toast.error;
  const dedupedError = (content, options = {}) =>
    originalError(content, typeof content === 'string' && !options.toastId ? { ...options, toastId: `err:${content}` } : options);
  dedupedError.__crmDeduped = true;
  toast.error = dedupedError;
}

/** ASP.NET model doğrulama yanıtı ({ errors: { Alan: ['…'] } }) → okunabilir mesaj */
const validationMessage = (data) => {
  const errors = data?.errors ?? data?.Errors;
  if (!errors || typeof errors !== 'object') return undefined;
  const list = Array.isArray(errors) ? errors : Object.values(errors).flat();
  const first = list.map((e) => (typeof e === 'string' ? e : e?.message || e?.errorMessage)).filter(Boolean);
  return first.length ? first.slice(0, 2).join(' ') : undefined;
};

const normalize = (response) => {
  const data = response?.data;
  // Global hata yakalayıcı PascalCase döner (StatusCode / Message)
  const statusCode = data?.statusCode ?? data?.StatusCode ?? response?.status ?? 500;
  return {
    statusCode,
    data: data?.data ?? data,
    message: data?.message || data?.Message || (statusCode !== 200 ? validationMessage(data) : undefined),
    token: data?.token || data?.data?.token,
    success: statusCode === 200,
    count: data?.count,
  };
};

const crmUnauthorized = () => {
  localStorage.removeItem('crmToken');
  localStorage.removeItem('crmUser');
  if (!window.location.pathname.startsWith('/crm/login')) {
    window.location.href = '/crm/login';
  }
};

const crmRequest = async (method, url, data, isFileUpload = false, options = {}) => {
  try {
    const response = await axios({
      method,
      url,
      data: method === 'get' || method === 'delete' ? undefined : data,
      params: method === 'get' || method === 'delete' ? data : undefined,
      headers: buildHeaders(isFileUpload, options.token),
      validateStatus: () => true,
    });

    if (response.status === 401 || response.data?.statusCode === 401) {
      // Odazen site token'ıyla yapılan çağrıda CRM oturumuna dokunma
      if (options.token) {
        const message = response.data?.message || 'Önce Odazen hesabınızla giriş yapın.';
        toast.error(message, { autoClose: 3500, theme: 'colored' });
        return { statusCode: 401, success: false, message };
      }
      crmUnauthorized();
      return { statusCode: 401, success: false, message: 'Unauthorized' };
    }

    const result = normalize(response);
    if (!result.success && result.message && !options.quiet) {
      toast.error(result.message, { autoClose: 3000, theme: 'colored' });
    }
    return result;
  } catch (error) {
    const message = error?.response?.data?.message || i18n.t('errors.general');
    if (!options.quiet) toast.error(message, { autoClose: 3000, theme: 'colored' });
    return { statusCode: error?.response?.status || 500, success: false, message };
  }
};

export const crmGet = (url, params) => crmRequest('get', url, params);
/** Hata toast'ı göstermeyen GET (çağıran sonucu kendisi yorumlar; ör. "başka ofisin talebi mi?" denemesi) */
export const crmGetQuiet = (url, params) => crmRequest('get', url, params, false, { quiet: true });
export const crmPost = (url, body) => crmRequest('post', url, body);
/** Belirli bir token ile POST (ör. Odazen site oturumu) */
export const crmPostWithToken = (url, body, token) => crmRequest('post', url, body, false, { token });
export const crmDelete = (url, params) => crmRequest('delete', url, params);
export const crmUpload = (url, formData) => crmRequest('post', url, formData, true);
export const crmPut = (url, body) => crmRequest('put', url, body);

/**
 * CRM oturumuyla ikili içerik (ör. portföy görseli) indirir → Blob; olmazsa null.
 * Hata toast'ı göstermez (çağıran sessizce yedeğe düşer). Canvas'ı "kirletmemek" için
 * görseller bu yolla blob → objectURL olarak yüklenir.
 */
export const crmGetBlob = async (url) => {
  try {
    const headers = buildHeaders();
    delete headers['Content-Type'];
    headers.accept = 'image/*,*/*';
    const response = await axios({ method: 'get', url, headers, responseType: 'blob', validateStatus: () => true });
    if (response.status === 401) return null;
    const blob = response.data;
    if (response.status !== 200 || !(blob instanceof Blob) || !blob.size) return null;
    // Sunucu hata gövdesini (ResultApi JSON / HTML) 200 ile dönebilir
    if (/json|text|html/i.test(blob.type || '')) return null;
    return blob;
  } catch {
    return null;
  }
};

export const saveCrmToken = (token) => {
  if (!token) return;
  localStorage.setItem('crmToken', encrypt(token));
};

export const getCrmAccessToken = () => {
  try {
    const encrypted = localStorage.getItem('crmToken');
    if (!encrypted) return null;
    return decrypt(encrypted);
  } catch {
    return null;
  }
};

export const clearCrmSession = () => {
  localStorage.removeItem('crmToken');
  localStorage.removeItem('crmUser');
};

export const readCrmUser = () => {
  try {
    const raw = localStorage.getItem('crmUser');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const writeCrmUser = (user) => {
  localStorage.setItem('crmUser', JSON.stringify(user));
};

/** JWT'nin süresi dolmuş mu? (exp yoksa ya da okunamazsa geçerli sayılır; sunucu yine doğrular) */
const isJwtExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
};

/**
 * Site tarafı (navbar vb.) için: geçerli CRM oturumu varsa kullanıcıyı döndürür, yoksa null.
 * CrmAuthProvider yalnızca /crm altında kurulu olduğundan doğrudan depodan okunur.
 */
export const getCrmSession = () => {
  const token = getCrmAccessToken();
  if (!token || isJwtExpired(token)) return null;
  const user = readCrmUser();
  if (!user) return null;
  const fullName = [user.name, user.lastName].filter(Boolean).join(' ').trim();
  return { user, fullName: fullName || user.agencyName || 'CRM', agencyName: user.agencyName || '' };
};
